/**
 * The client report, shaped for the output screen.
 *
 * `GET /reviews/{id}/report` is the report's single source: the checklist, the
 * list, the document view and the PDF/CSV exports are all projections of it,
 * so the screen reads its findings from here rather than from the workspace's
 * review payload. The one thing the report does not carry is every on-label
 * location of a finding — only the first — so those are joined in from
 * `GET /reviews/{id}` by finding id (see {@link withLocations}).
 */

import type { components } from "@/types/api.gen"

import type { Finding, FindingId, PointRect, ReviewId } from "../data/review-data"

/* ── Wire ──────────────────────────────────────────────────────────────── */

export type WireReportFinding = components["schemas"]["ReportFinding"]
export type WireReport = components["schemas"]["ReportOut"]

export type ReportView = components["schemas"]["ReportView"]
export type ReportVerdict = components["schemas"]["Verdict"]
export type ClientResolution = components["schemas"]["ClientResolution"]

/* ── UI ────────────────────────────────────────────────────────────────── */

/**
 * The report's own severity vocabulary: an escalation is its own class, not a
 * kind of error — "this is wrong" and "we could not determine this" are
 * different messages to a client.
 */
export type ReportSeverity = "error" | "warn" | "needs_sme"

/**
 * The finding's severity as the legacy client report words it — by what the
 * client should do, not the raw level. An escalation needs a specialist, an
 * error must be corrected, anything else is a review item.
 */
export const REPORT_SEVERITY_ACTION_LABEL: Record<ReportSeverity, string> = {
  error: "Must correct",
  warn: "Review",
  needs_sme: "Needs specialist review",
}

export type FindingState = components["schemas"]["FindingState"]

export interface ReportFindingItem {
  id: FindingId
  /** Position on the label, shared with the workspace. */
  number: number
  /** What the report prints: a reviewer's rewording when there is one, else the generated text. */
  title: string
  severity: ReportSeverity
  /** Where the finding is in its review lifecycle; corrected ones only list with "Show corrected". */
  state: FindingState
  /** The specialist's disposition, when one has been recorded. */
  smeDecision: string | null
  sectionId: string | null
  sectionTitle: string | null
  groupNumber: number | null
  groupName: string | null
  /** How many places on the label carry this divergence. */
  occurrenceCount: number
  printedText: string | null
  masterText: string | null
  /** The narrated divergence, for a finding that captured neither side separately. */
  divergence: string | null
  ruleCheck: string | null
  remediation: string | null
  citations: string[]
  clientResolution: ClientResolution | null
  /**
   * Located printed-label occurrences, first to last. The report itself names
   * only the first; {@link withLocations} fills in the rest.
   */
  locations: PointRect[]
}

/**
 * One report-level statement about the run itself.
 *
 * `unreadable` and `ocr` are facts about the file we were given — the whole of
 * it and a named part of it; `unavailable` is one about our own run.
 */
export interface ScopeNotice {
  kind: "unreadable" | "ocr" | "unavailable"
  text: string
}

/**
 * Which master the findings were produced from.
 *
 * `inherited` is a resubmission's master, carried over from the previous
 * version: its own value, so the report never credits a client with a file
 * they did not send this time. `null` is the honest unknown.
 */
export interface ReportMaster {
  source: "ppls" | "upload" | "inherited" | null
  filename: string | null
  /** How many candidate masters the review weighed. */
  candidateCount: number
  /** `YYYYMMDD`, parsed off the PPLS file name. PPLS-resolved masters only. */
  stampDate: string | null
  /** ISO. PPLS-resolved masters only. */
  acceptedDate: string | null
  /** Caveats PPLS resolution recorded — no FPL date supplied, stamp type unverified. */
  reasons: string[]
}

/** Requirements that went unevaluated for the same reason. */
export interface CoverageGap {
  lane: string
  label: string
  count: number
  ruleIds: string[]
}

/** What this review checked, and what it did not. */
export interface ReportCoverage {
  /** False for a review run before scope was recorded: we do not know what it covered. */
  recorded: boolean
  /** Set when no page of the label could be read. */
  unreadable: boolean
  checked: number
  answerable: number
  checkedRuleIds: string[]
  /** Put to the review and answered: they do not govern this label. */
  notApplicableRuleIds: string[]
  /** Put to a stage that did not complete — a gap in the run, not the label. */
  unansweredRuleIds: string[]
  gaps: CoverageGap[]
}

/** The heading for findings the report places in no section. */
export const UNSECTIONED_TITLE = "Across the label"

/** One FIFRA section of the report, with its findings in the server's order. */
export interface ReportSectionItem {
  sectionId: string | null
  title: string
  findingIds: FindingId[]
}

export interface Report {
  reviewId: ReviewId
  regNo: string
  productName: string | null
  versionNo: number
  view: ReportView
  verdict: ReportVerdict
  /** The prose summary, one entry per paragraph. */
  summaryParagraphs: string[]
  master: ReportMaster | null
  /** Every divergence on the report, as the severity rollup counts them. */
  divergenceTotal: number
  /** The rollup per severity: which filters the list offers. Outstanding work only. */
  severityCounts: Record<ReportSeverity, number>
  coverage: ReportCoverage
  /**
   * In the server's priority order: escalations, errors, warnings, then label
   * position. Kept as sent, not re-sorted, so the list cannot disagree with the
   * PDF and CSV. The checklist walks them by number instead ({@link byNumber}).
   */
  findings: ReportFindingItem[]
  /** In label reading order, as the server groups them. */
  sections: ReportSectionItem[]
  /** What the report must say about its own run, above every tab. Empty for a complete run. */
  scopeNotices: ScopeNotice[]
}

/* ── Adapter ───────────────────────────────────────────────────────────── */

function severityOf(f: WireReportFinding): ReportSeverity {
  if (f.expected_system_output === "needs_sme") return "needs_sme"
  return f.severity === "error" ? "error" : "warn"
}

/** The report's 0-based page and point box, as the 1-based rect the PDF layer uses. */
function primaryLocation(f: WireReportFinding): PointRect | null {
  if (!f.bbox || f.page == null) return null
  const [x0, y0, x1, y1] = f.bbox
  return { page: f.page + 1, x0, y0, x1, y1 }
}

export function fromWireReportFinding(
  f: WireReportFinding,
  sectionTitleById: ReadonlyMap<string, string>,
): ReportFindingItem {
  const primary = primaryLocation(f)
  return {
    id: String(f.id) as FindingId,
    number: f.number,
    title: f.summary,
    severity: severityOf(f),
    state: f.state,
    smeDecision: f.sme_decision ?? null,
    sectionId: f.section_id ?? null,
    sectionTitle: f.section_id ? (sectionTitleById.get(f.section_id) ?? f.section_id) : null,
    groupNumber: f.group_number ?? null,
    groupName: f.group_name ?? null,
    occurrenceCount: Math.max(1, f.occurrence_count),
    printedText: f.fpl_text ?? null,
    masterText: f.master_text ?? null,
    divergence: f.divergence ?? null,
    ruleCheck: f.rule_check ?? null,
    remediation: f.remediation ?? null,
    citations: f.citation ?? [],
    clientResolution: f.client_resolution ?? null,
    locations: primary ? [primary] : [],
  }
}

/**
 * The report-level notices a run has earned, in the order they are shown.
 *
 * Widest first about the document, then about our run: `unreadable` says no
 * page could be trusted, `ocr` names the pages that were not read reliably,
 * `unavailable` says our own stages did not get to run. They can co-occur and
 * none suppresses another — each is a separate, true statement, and dropping
 * one would silently tell a client the whole fault lay elsewhere. Ported as-is
 * from the legacy console's `scopeNotices`.
 */
export function scopeNoticesOf(coverage: WireReport["coverage"]): ScopeNotice[] {
  const notices: ScopeNotice[] = []
  if (!coverage) return notices
  if (coverage.unreadable) notices.push({ kind: "unreadable", text: coverage.unreadable })
  if (coverage.ocr) notices.push({ kind: "ocr", text: coverage.ocr })
  if (coverage.unavailable) notices.push({ kind: "unavailable", text: coverage.unavailable })
  return notices
}

/** The backend joins paragraphs with a blank line; blank runs are dropped, not rendered empty. */
export function summaryParagraphsOf(summary: string | null | undefined): string[] {
  return (summary ?? "").split("\n\n").filter((para) => para.trim() !== "")
}

function fromWireMaster(m: WireReport["master"]): ReportMaster | null {
  if (!m) return null
  return {
    source: m.source ?? null,
    filename: m.filename ?? null,
    candidateCount: m.candidate_count,
    stampDate: m.stamp_date ?? null,
    acceptedDate: m.accepted_date ?? null,
    reasons: m.reasons ?? [],
  }
}

/**
 * Coverage, with every list defaulted.
 *
 * The backend always sends these, but this console deploys separately and can
 * run against a backend a release behind. Absent coverage reads as "scope not
 * recorded" — never as zero, which would print a confident sentence about a
 * review that never asked.
 */
function fromWireCoverage(c: WireReport["coverage"]): ReportCoverage {
  return {
    recorded: c?.recorded ?? false,
    unreadable: Boolean(c?.unreadable),
    checked: c?.checked ?? 0,
    answerable: c?.answerable ?? 0,
    checkedRuleIds: c?.checked_rule_ids ?? [],
    notApplicableRuleIds: c?.not_applicable_rule_ids ?? [],
    unansweredRuleIds: c?.unanswered_rule_ids ?? [],
    gaps: (c?.gaps ?? []).map((g) => ({
      lane: g.lane,
      label: g.label,
      count: g.count,
      ruleIds: g.rule_ids ?? [],
    })),
  }
}

/** A section's heading. An empty title is no title, as in the legacy console. */
function sectionTitleOf(s: { section_id: string | null; title: string }): string {
  if (s.title) return s.title
  if (s.section_id) return s.section_id
  return UNSECTIONED_TITLE
}

export function fromWireReport(w: WireReport): Report {
  const sectionTitleById = new Map<string, string>()
  for (const s of w.sections ?? []) {
    if (s.section_id && s.title) sectionTitleById.set(s.section_id, s.title)
  }
  return {
    reviewId: String(w.review_id) as ReviewId,
    regNo: w.reg_nr,
    productName: w.product_name ?? null,
    versionNo: w.version_no,
    view: w.view,
    verdict: w.verdict,
    summaryParagraphs: summaryParagraphsOf(w.summary),
    master: fromWireMaster(w.master),
    divergenceTotal: w.severity_rollup?.total ?? 0,
    severityCounts: {
      needs_sme: w.severity_rollup?.needs_sme ?? 0,
      error: w.severity_rollup?.error ?? 0,
      warn: w.severity_rollup?.warn ?? 0,
    },
    coverage: fromWireCoverage(w.coverage),
    findings: (w.findings ?? []).map((f) => fromWireReportFinding(f, sectionTitleById)),
    sections: (w.sections ?? []).map((s) => ({
      sectionId: s.section_id,
      title: sectionTitleOf(s),
      findingIds: s.findings.map((f) => String(f.id) as FindingId),
    })),
    scopeNotices: scopeNoticesOf(w.coverage),
  }
}

/**
 * Join every printed-label occurrence onto the report's findings.
 *
 * Matched by id, never by position: the review payload and the report order
 * their findings differently. A finding the review payload has no locations
 * for keeps the report's own first box.
 *
 * Only for a finding the report itself places. The report leaves a finding
 * unpinned on purpose — "not found anywhere on the label" has no place to
 * point at — and the occurrence rows such a finding can still carry sit on
 * other findings' lines. Pinning it from them would mark a place the report
 * says it does not have, and cover the findings that are there.
 */
export function withLocations(
  items: readonly ReportFindingItem[],
  reviewFindings: readonly Finding[],
): ReportFindingItem[] {
  const rectsById = new Map(reviewFindings.map((f) => [f.id, f.occurrenceRects]))
  return items.map((item) => {
    if (item.locations.length === 0) return item
    const rects = rectsById.get(item.id)
    return rects && rects.length > 0 ? { ...item, locations: rects } : item
  })
}

/** By position on the label — the order the checklist walks. */
export function byNumber(items: readonly ReportFindingItem[]): ReportFindingItem[] {
  return [...items].sort((a, b) => a.number - b.number)
}

/** Done as far as the client is concerned. Mirrors the backend's `is_corrected`. */
export function isCorrected(item: Pick<ReportFindingItem, "state">): boolean {
  return item.state === "corrected_confirmed" || item.state === "resolved"
}

/** The client has answered this finding: reviewed it, or disputed it. */
export function hasClientResponse(item: ReportFindingItem): boolean {
  return item.clientResolution === "done" || item.clientResolution === "disputed"
}

/* ── Summary ───────────────────────────────────────────────────────────── */

/**
 * The date a PPLS-resolved master carries, as ISO.
 *
 * `stampDate` arrives as `YYYYMMDD` and `acceptedDate` as ISO; they share one
 * slot, so the stamp is reformatted rather than printed raw. An empty stamp is
 * no stamp, and falls through to the accepted date. Ported from the legacy
 * console's `masterDate`.
 */
export function masterDate(master: ReportMaster | null): string | null {
  if (master?.source !== "ppls") return null
  const stamp = master.stampDate
  if (stamp && /^\d{8}$/.test(stamp)) {
    return `${stamp.slice(0, 4)}-${stamp.slice(4, 6)}-${stamp.slice(6)}`
  }
  if (stamp) return stamp
  if (master.acceptedDate) return master.acceptedDate
  return null
}

/**
 * How this review came by its master, as a phrase to follow the filename.
 *
 * Mirrors the PDF of the same review: a filename alone cannot tell an upload
 * from a carried-over master from one nobody recorded. A stamp and an accepted
 * date are named apart, since PPLS publishes a true stamp date for only some
 * masters. Ported from the legacy console's `masterProvenance`.
 */
export function masterProvenance(master: ReportMaster | null): string | null {
  if (!master) return null
  let detail: string
  if (master.source === "ppls") {
    const date = masterDate(master)
    detail = date ? `EPA PPLS, ${master.stampDate ? "stamped" : "accepted"} ${date}` : "EPA PPLS"
  } else if (master.source === "upload") {
    detail = "uploaded"
  } else if (master.source === "inherited") {
    detail = "carried over from the previous version"
  } else {
    detail = "source not recorded"
  }
  return master.candidateCount > 1
    ? `${detail}; 1 of ${String(master.candidateCount)} candidate masters`
    : detail
}

/** The slice of `GET /reviews/{id}/documents` the summary reads. */
export interface ReviewDocumentEntry {
  role: string
  isGoverning: boolean
  filename: string | null
}

/**
 * The "Master" line: the filename and where it came from.
 *
 * The report's own master block answers this; the review's document set only
 * stands in for a filename the report does not carry — the governing master,
 * else the first. No date is taken from a document row: the only one it holds
 * is the printed label's, which here would read as the master's. Same fallback
 * order and wording as the legacy console.
 */
export function masterLabelOf(
  master: ReportMaster | null,
  documents: readonly ReviewDocumentEntry[],
): string {
  const masterDocs = documents.filter((d) => d.role === "master")
  const governing = masterDocs.find((d) => d.isGoverning) ?? masterDocs[0]
  const name = master?.filename ?? governing?.filename
  if (name) {
    const provenance = masterProvenance(master)
    return provenance ? `${name} · ${provenance}` : name
  }
  return master?.source === "ppls" ? "Resolved from EPA PPLS" : "No master recorded"
}

/** The "Printed label" line: the FPL's filename, else what a filename-less FPL is. */
export function printedLabelOf(documents: readonly ReviewDocumentEntry[]): string {
  return documents.find((d) => d.role === "fpl")?.filename ?? "Market specimen"
}

/**
 * Which of three statements the scope block makes.
 *
 * `unrecorded` — we do not know what this review covered; it outranks
 * `unreadable`, since that says we do know. `unreadable` — only the checks that
 * never read the label's text ran, so a tally would read as a share of a review
 * that did not happen. `counts` — the ordinary case.
 */
export type ScopeTally = "unrecorded" | "unreadable" | "counts"

export function scopeTallyOf(coverage: ReportCoverage): ScopeTally {
  if (!coverage.recorded) return "unrecorded"
  if (coverage.unreadable) return "unreadable"
  return "counts"
}

/** Requirements no stage examined, summed across lanes. */
export function gapTotalOf(coverage: ReportCoverage): number {
  return coverage.gaps.reduce((sum, gap) => sum + gap.count, 0)
}
